import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from './BusinessOnboarding.module.css'; 
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../components/loader2';
import showToast from "../components/ShowToast";
import Swal from 'sweetalert2';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";

import {
  ArrowLeft, Link2, FileText, Camera, Briefcase, Target, ShieldCheck, Award,
  Upload, Plus, X, Search, MapPin, Phone, Home, LogOut, Package, GraduationCap, Lock, Info,
} from "lucide-react";

const BusinessOnboardingNew = () => {
  const navigate = useNavigate();
  const { businessId: businessIdFromParams } = useParams(); 
  const [username, setUsername] = useState('');

  const [businessUserId, setBusinessUserId] = useState(null);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setBusinessUserId(decodedToken.nameid);
      setBusinessId(decodedToken.BusinessId);
      setUsername(decodedToken.UserName || 'User'); 
    }
  }, []);

  // --- State Variables ---
  const [addresses, setAddresses] = useState([]);
  const [currentAddressInput, setCurrentAddressInput] = useState('');
  const [currentMobileInput, setCurrentMobileInput] = useState('');
  const [currentLandlineInput, setCurrentLandlineInput] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  const [businessName, setBusinessName] = useState(localStorage.getItem('businessName') || '');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [abn, setAbn] = useState('');
  const [acn, setAcn] = useState('');
  const [shortBio, setShortBio] = useState('');
  const [longBio, setLongBio] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageBase64, setProfileImageBase64] = useState(null);
  const [coverImageBase64, setCoverImageBase64] = useState(null);
  const [preview, setPreview] = useState(null);

  const [businessType, setBusinessType] = useState('');
  const [scope, setScope] = useState({ residential: false, commercial: false, government: false });
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [professionSearchTerm, setProfessionSearchTerm] = useState('');
  const [professionResults, setProfessionResults] = useState([]);
  const [isProfessionDropdownOpen, setIsProfessionDropdownOpen] = useState(false);
  const professionDropdownRef = useRef(null);

  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceResults, setServiceResults] = useState([]);
  const [showAddServicePopup, setShowAddServicePopup] = useState(false);
  const [newService, setNewService] = useState({ name: '', professionId: '' });
  const [addServiceLoading, setAddServiceLoading] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productResults, setProductResults] = useState([]);

  const [serviceAfterHours, setServiceAfterHours] = useState(false);
  const [serviceOnWeekends, setServiceOnWeekends] = useState(false);

  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [isFullyInsured, setIsFullyInsured] = useState(false);
  const [availableCertifications, setAvailableCertifications] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);

  const [registrationNumber, setRegistrationNumber] = useState('');
  const [yearsExperience, setYearsExperience] = useState(1);
  const [availableQualifications, setAvailableQualifications] = useState([]);
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  const [availableMemberships, setAvailableMemberships] = useState([]);
  const [selectedMemberships, setSelectedMemberships] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(!!businessIdFromParams);
  const isInitialMount = useRef(true);

  // --- Handlers ---

  const handleAddNewService = async () => {
    if (!newService.name.trim()) return showToast('error', 'Please enter a service name.');
    if (!newService.professionId) return showToast('error', 'Please select a profession.');
    
    setAddServiceLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/Professional/createService`, {
        name: newService.name,
        professionId: newService.professionId,
        description: newService.name,
      });

      if (response.status === 201 && response.data && response.data.id) {
        const newlyAddedService = { id: response.data.id, name: response.data.name };
        setSelectedServices(prev => [...prev, newlyAddedService]);
        showToast('success', 'Service added successfully!');
        setShowAddServicePopup(false);
        setNewService({ name: '', professionId: '' });
      }
    } catch (error) {
      showToast('error', 'Failed to add service.');
    } finally {
      setAddServiceLoading(false);
    }
  };

  const handleButtonClick = (action) => {
      if (action === 'logout') {
          localStorage.removeItem('token');
          window.location.href = '/login';
      }
  };

  // Fetch Logic
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      
      const decodedToken = jwtDecode(token);
      if (decodedToken.UserType !== 'Business') {
        showToast('error', 'Unauthorized');
        navigate('/login');
        return;
      }

      setBusinessUserId(decodedToken.nameid);
      setBusinessId(decodedToken.BusinessId);
      setIsLoading(true);

      try {
        if (businessIdFromParams) {
          if (businessIdFromParams === String(decodedToken.BusinessId)) {
            setIsReadOnly(false);
          } else {
            setIsReadOnly(true);
          }
          
          const profileResponse = await axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessProfile?businessId=${businessIdFromParams}`);
          const locationsResponse = await axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessLocations?businessId=${businessIdFromParams}`);

          if (profileResponse.data && profileResponse.data.businesses) {
            const businessData = profileResponse.data.businesses;
            setBusinessName(businessData.businessName);
            setBusinessWebsite(businessData.website);
            setAbn(businessData.abn);
            setAcn(businessData.acn);
            setShortBio(businessData.description);
            setLongBio(businessData.profileDescription);
            setBusinessType(businessData.businessType || '');
            setScope({
              residential: businessData.residential === 1,
              commercial: businessData.commercial === 1,
              government: businessData.government === 1,
            });
            setServiceAfterHours(businessData.afterHours === 1);
            setServiceOnWeekends(businessData.weekends === 1);
            setLicenseNumber(businessData.licenseNumber);
            setLicenseExpiry(businessData.formattedLicenseExpiry);
            setIsFullyInsured(businessData.fullyInsured);
            setRegistrationNumber(businessData.registrationNumber);
            setYearsExperience(businessData.yearsExperience);

            if (businessData.profilePic) {
              setPreview(businessData.profilePic);
              setProfileImageBase64(businessData.profilePic); 
            }

            if (businessData.coverImage) {
              setCoverImageBase64(businessData.coverImage);
            }
            
            setSelectedProfessions(profileResponse.data.professions || []);
            setSelectedServices(profileResponse.data.services || []);
            setSelectedProducts((profileResponse.data.products || []).map(p => ({ id: p.productId, name: p.productName })));
            
            // Populate arrays for selected items
            setSelectedCertifications(profileResponse.data.certifications.map(c => c.certificationId) || []);
            setSelectedQualifications(profileResponse.data.qualificationDtos.map(q => q.qualificationId) || []);
            setSelectedMemberships(profileResponse.data.memberships?.map(m => m.membershipId) || []);
          }

          if (locationsResponse.data) {
            setAddresses(locationsResponse.data.map(l => ({
              address: `${l.streetNumber} ${l.streetName}, ${l.suburb}, ${l.myState} ${l.postcode}`,
              mobile: l.phone,
              landline: l.landline,
              isPrimary: l.isPrimary === 1
            })));
          }
        }
        isInitialMount.current = false;
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [businessIdFromParams, navigate]);

  // Fetch dependents logic (professions -> services/certs/quals/memberships)
  useEffect(() => {
    const fetchProfessionDependentData = async () => {
      if (selectedProfessions.length === 0) {
        setAvailableCertifications([]); 
        setAvailableQualifications([]);
        setAvailableMemberships([]);
        return;
      }

      // We only set loading true for this specific background fetch if needed, 
      // but usually for onboarding it's better to be subtle or show a small indicator.
      const professionIds = selectedProfessions.map(p => p.id || p.professionId);
      const params = new URLSearchParams();
      professionIds.forEach(id => params.append('professionIds', id));
      const queryString = params.toString();

      try {
        const [certs, quals, memberships] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/certifications-by-professions?${queryString}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/qualifications-by-professions?${queryString}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/memberships-by-professions?${queryString}`)
        ]);
        setAvailableCertifications(certs.data);
        setAvailableQualifications(quals.data);
        setAvailableMemberships(memberships.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProfessionDependentData();
  }, [selectedProfessions]);

  // Search Helpers
  const handleAddressSearchChange = async (e) => {
    const searchValue = e.target.value;
    setCurrentAddressInput(searchValue);
    if (searchValue.length > 2) {
      setIsSearchingAddress(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/addresscomplete`, { params: { input: searchValue } });
        setAddressResults(response.data?.predictions || []);
      } catch (error) { console.error(error); } 
      finally { setIsSearchingAddress(false); }
    } else { setAddressResults([]); }
  };

  const handleSelectAddress = (suggestion) => {
    setCurrentAddressInput(suggestion.description);
    setAddressResults([]);
  };


const handleAddAddress = () => {
    if (!currentAddressInput.trim()) return showToast('error', 'Please enter an address.');

    // 1. Clean and Validate Mobile
    const cleanedMobile = currentMobileInput.replace(/\D/g, '');

    if (cleanedMobile.length !== 10) {
        if (cleanedMobile.length > 0) {
            showToast('error', 'Mobile number must be exactly 10 digits.');
            return;
        }
        showToast('error', 'Mobile number is required.');
        return;
    }

    const newAddress = {
      address: currentAddressInput.trim(),
      mobile: cleanedMobile, // Save the clean 10-digit version
      landline: currentLandlineInput.trim(),
      isPrimary: addresses.length === 0,
    };

    setAddresses([...addresses, newAddress]);
    setCurrentAddressInput('');
    setCurrentMobileInput('');
    setCurrentLandlineInput('');
  };
  const handleSetPrimary = (indexToMakePrimary) => {
    const updated = addresses.map((addr, index) => ({
      ...addr,
      isPrimary: index === indexToMakePrimary,
    }));
    setAddresses(updated);
  };

  const handleRemoveAddress = (indexToRemove) => {
    const isRemovingPrimary = addresses[indexToRemove]?.isPrimary;
    let updated = addresses.filter((_, i) => i !== indexToRemove);
    if (isRemovingPrimary && updated.length > 0) updated[0].isPrimary = true;
    setAddresses(updated);
  };

  // Search Professions/Services
  const handleProfessionSearch = async (val) => {
    setProfessionSearchTerm(val);
    if (val.length > 0) {
        const res = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchprofessions?search=${val}`);
        setProfessionResults(res.data);
    } else setProfessionResults([]);
  };

  const handleSelectProfession = async (item) => {
    if (selectedProfessions.some((i) => i.id === item.id)) return;
    setSelectedProfessions(prev => [...prev, item]);
    setProfessionSearchTerm('');
    setProfessionResults([]);

    // --- RESTORED LOGIC: Auto-add services for this profession ---
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/servicesByProfession/${item.id}`);
      const services = response.data;
      if (services && services.length > 0) {
        setSelectedServices(prev => {
          const ids = new Set(prev.map(s => s.id || s.serviceId));
          const newS = services.filter(s => !ids.has(s.id));
          return [...prev, ...newS];
        });
        showToast('success', `Added standard services for ${item.name}`);
      }
    } catch (e) { console.error(e); }
  };

  const handleServiceSearch = async (val) => {
      setServiceSearchTerm(val);
      if(val.length > 0) {
          const res = await axios.get(`${API_BASE_URL}/api/Search/searchservices?search=${val}`);
          setServiceResults(res.data);
      } else setServiceResults([]);
  };

  const handleSelectService = (item) => {
      if (!selectedServices.some(i => (i.id || i.serviceId) === item.id)) {
          setSelectedServices([...selectedServices, item]);
      }
      setServiceSearchTerm('');
      setServiceResults([]);
  }

  const handleProductSearch = async (val) => {
      setProductSearchTerm(val);
      if (val.length > 0) {
          try {
              const res = await axios.get(`${API_BASE_URL}/api/Search/searchproducts?search=${val}`);
              setProductResults(res.data);
          } catch (e) { console.error(e); }
      } else {
          setProductResults([]);
      }
  };

  const handleSelectProduct = (item) => {
      if (!selectedProducts.some((i) => i.id === item.id)) {
          setSelectedProducts(prev => [...prev, item]);
      }
      setProductSearchTerm('');
      setProductResults([]);
  };

  const handleRemoveProduct = (productId) => {
      setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleRemoveProfession = (id) => setSelectedProfessions(prev => prev.filter(p => (p.id || p.professionId) !== id));
  const handleRemoveService = (id) => setSelectedServices(prev => prev.filter(s => (s.id || s.serviceId) !== id));

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => setProfileImageBase64(reader.result);
      reader.readAsDataURL(file);
      setPreview(URL.createObjectURL(file));
      setProfileImage(file);
    }
  };

  const handleCoverUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => setCoverImageBase64(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // --- RESTORED HANDLERS ---
  const handleCertificationChange = (id) => {
    setSelectedCertifications((prev) =>
      prev.includes(id) ? prev.filter((cId) => cId !== id) : [...prev, id]
    );
  };

  const handleQualificationChange = (id) => {
    setSelectedQualifications((prev) =>
      prev.includes(id) ? prev.filter((qId) => qId !== id) : [...prev, id]
    );
  };

  const handleMembershipChange = (id) => {
    setSelectedMemberships((prev) =>
      prev.includes(id) ? prev.filter((mId) => mId !== id) : [...prev, id]
    );
  };
  // -------------------------

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessLocations`, {
            businessId,
            locations: addresses.map(addr => ({
                address: addr.address,
                mobile: addr.mobile,
                landline: addr.landline,
                isPrimary: !!addr.isPrimary,
            }))
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateBusinessOnboarding02`, {
            businessUserId, website: businessWebsite, acn, abn,
            profileImage: profileImageBase64, coverImage: coverImageBase64, description: shortBio, profileDescription: longBio
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessOnboarding03`, {
            businessUserId, businesstype: businessType,
            residential: scope.residential ? 1 : 0, commercial: scope.commercial ? 1 : 0, government: scope.government ? 1 : 0,
            afterHours: serviceAfterHours ? 1 : 0, weekends: serviceOnWeekends ? 1 : 0,
            professionIds: selectedProfessions.map(p => p.professionId || p.id).filter(Boolean),
            ancillaryIds: []
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessservices`, {
            businessUserId, serviceIds: selectedServices.map(s => s.serviceId || s.id).filter(Boolean)
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessproducts`, {
            businessUserId, productIds: selectedProducts.map(p => p.id).filter(Boolean)
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveaccreditation`, {
            businessUserId, licenseNumber, licenseExpiry: licenseExpiry || null,
            fullyInsured: isFullyInsured, certificationIds: selectedCertifications,
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveQualificationsAndMemberships`, {
            businessUserId, registrationNumber, yearsExperience: parseInt(yearsExperience) || 1,
            qualificationIds: selectedQualifications, membershipIds: selectedMemberships,
        });

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateOnboardingBusiness?businessUserId=${businessUserId}`);
        
        Swal.fire({
            title: "Success!", text: "Profile updated successfully.", icon: "success", confirmButtonText: "Go to Dashboard"
        }).then(() => navigate('/businessdash'));

    } catch (err) {
        showToast('error', 'Failed to save profile.');
        console.error(err);
    } finally {
        setIsLoading(false);
    }
  };


const sanitizeUrl = (url) => {
    if (!url) return '';
    // Remove whitespace
    let newUrl = url.trim();
    // If it doesn't start with http:// or https://, add https://
    if (!/^https?:\/\//i.test(newUrl)) {
        newUrl = `https://${newUrl}`;
    }
    return newUrl;
};

  return (
    <div className={styles.pageContainer}>
      
      <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'profile'} />

      {/* GOLD HEADER BAR */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            {businessIdFromParams && (
              <button onClick={() => navigate(-1)} className={styles.btnGhost}>
                <ArrowLeft className={styles.iconMd} />
              </button>
            )}
            <div className={styles.headerLogo}>
              <Home className={styles.iconMd} color="#022d6b" /> 
              <span>Build Start</span>
            </div>
            <div className={styles.separator}></div>
            <div>
              <h1 className={styles.headerTitle}>{businessIdFromParams ? "Edit Business Profile" : "Business Onboarding"}</h1>
              <p className={styles.headerSubtitle}>{businessIdFromParams ? "Update your details" : "Complete your profile"}</p>
            </div>
          </div>
          
          <button onClick={() => handleButtonClick('logout')} className={styles.btnGhostText}>
            <LogOut size={18} /> Logout
          </button>
        </div>
      </header>

      <div className={styles.mainContent}>
        {isLoading && <Loader2 />}
        
        <div className={styles.grid}>
          {/* LEFT COLUMN: FORMS */}
          <div className={styles.colMain}>

            {/* Business Details */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                    <Briefcase color="#2563eb" /> Business Details
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.gridCols2}>

<div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
        <label className={styles.label} style={{ marginBottom: 0 }}>Business Name</label>
        <Lock size={14} color="#94a3b8" />
    </div>
    
    <input 
        type="text" 
        value={businessName} 
        className={styles.input} 
        disabled 
        style={{ backgroundColor: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }} 
    />
    
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '6px' }}>
        <Info size={14} color="#3b82f6" style={{ marginTop: '2px', flexShrink: 0 }} />
        <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
            To change your legal business name, please{' '}
            <a 
                href="/contact-support" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}
            >
                contact support
            </a>
            {' '}for verification.
        </p>
    </div>
</div>

                    {/*
                    <div>
                        <label className={styles.label}>Website</label>
                        <input type="text" value={businessWebsite} onChange={(e) => setBusinessWebsite(e.target.value)} className={styles.input} placeholder="https://..." />
                    </div>

                    */}

                  <div>
                      <label className={styles.label}>Website</label>
                      <input 
                          type="text" 
                          value={businessWebsite} 
                          onChange={(e) => setBusinessWebsite(e.target.value)} 
                          onBlur={(e) => {
                              // Auto-fix the URL when the user leaves the field
                              const fixedUrl = sanitizeUrl(e.target.value);
                              setBusinessWebsite(fixedUrl);
                          }}
                          className={styles.input} 
                          placeholder="www.yourbusiness.com.au" // Simplified placeholder
                      />
                      {/* Optional: Add a helper text if invalid format detected after fix */}
                  </div>

                </div>
                <div className={styles.gridCols2}>
                    <div>
                        <label className={styles.label}>ABN</label>
                        <input type="text" value={abn} onChange={(e) => setAbn(e.target.value)} className={styles.input} />
                    </div>
                    <div>
                        <label className={styles.label}>ACN</label>
                        <input type="text" value={acn} onChange={(e) => setAcn(e.target.value)} className={styles.input} />
                    </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}><FileText color="#d97706" /> Description</div>
              </div>
              <div className={styles.cardContent}>
                <div>
                    <label className={styles.label}>Short Bio</label>
                    <input type="text" value={shortBio} onChange={(e) => setShortBio(e.target.value)} className={styles.input} placeholder="One line description..." />
                </div>
                <div>
                    <label className={styles.label}>Detailed Description</label>
                    <textarea value={longBio} onChange={(e) => setLongBio(e.target.value)} className={styles.textarea} rows={4} placeholder="Full business description..." />
                </div>
              </div>
            </div>

            {/* Professions */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><Target color="#7c3aed" /> Professions</div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.inputWithIcon} ref={professionDropdownRef}>
                        <Search className={styles.inputIcon} size={16} />
                        <input 
                            className={`${styles.input} ${styles.inputPl}`} 
                            placeholder="Add professions..." 
                            value={professionSearchTerm}
                            onChange={(e) => { setProfessionSearchTerm(e.target.value); handleProfessionSearch(e.target.value); }}
                            onFocus={() => setIsProfessionDropdownOpen(true)}
                        />
                        {isProfessionDropdownOpen && professionResults.length > 0 && (
                            <ul className={styles.dropdownResults}>
                                {professionResults.map(item => (
                                    <li key={item.id} className={styles.resultItem} onClick={() => handleSelectProfession(item)}>{item.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className={styles.flexWrap}>
                        {selectedProfessions.map(p => (
                            <span key={p.id || p.professionId} className={styles.badge}>
                                {p.name} <button className={styles.badgeBtn} onClick={() => handleRemoveProfession(p.id || p.professionId)}><X size={14}/></button>
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Services */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><Award color="#059669" /> Services</div>
                    <button className={styles.toggleBtn} onClick={() => setShowAddServicePopup(true)}>+ Add Custom Service</button>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.inputWithIcon}>
                        <Search className={styles.inputIcon} size={16} />
                        <input 
                            className={`${styles.input} ${styles.inputPl}`} 
                            placeholder="Search services..." 
                            value={serviceSearchTerm}
                            onChange={(e) => { setServiceSearchTerm(e.target.value); handleServiceSearch(e.target.value); }}
                        />
                        {serviceResults.length > 0 && (
                            <ul className={styles.dropdownResults}>
                                {serviceResults.map(item => (
                                    <li key={item.id} className={styles.resultItem} onClick={() => handleSelectService(item)}>{item.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className={styles.flexWrap}>
                        {selectedServices.map(s => (
                            <span key={s.id || s.serviceId} className={styles.badge}>
                                {s.name} <button className={styles.badgeBtn} onClick={() => handleRemoveService(s.id || s.serviceId)}><X size={14}/></button>
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Products */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><Package color="#f59e0b" /> Products</div>
                </div>
                <div className={styles.cardContent}>
                    <div>
                        <label className={styles.label}>What products do you provide? (Optional)</label>
                        <div className={styles.inputWithIcon}>
                            <Search className={styles.inputIcon} size={16} />
                            <input 
                                className={`${styles.input} ${styles.inputPl}`} 
                                placeholder="Search products (e.g. Tiles, Paint)..." 
                                value={productSearchTerm}
                                onChange={(e) => { 
                                    setProductSearchTerm(e.target.value); 
                                    handleProductSearch(e.target.value); 
                                }}
                            />
                            {productResults.length > 0 && (
                                <ul className={styles.dropdownResults}>
                                    {productResults.map(item => (
                                        <li key={item.id} className={styles.resultItem} onClick={() => handleSelectProduct(item)}>
                                            {item.name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className={styles.flexWrap}>
                            {selectedProducts.map(p => (
                                <span key={p.id} className={styles.badge}>
                                    {p.name} 
                                    <button className={styles.badgeBtn} onClick={() => handleRemoveProduct(p.id)}>
                                        <X size={14}/>
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Locations */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><MapPin color="#dc2626" /> Locations</div>
                </div>
                <div className={styles.cardContent}>
                    {addresses.map((addr, idx) => (
                        <div key={idx} className={styles.locationItem}>
                            <div>
                                <strong>{addr.address}</strong>
                                {addr.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                                <div style={{fontSize:'0.85rem', color:'#64748b'}}>{addr.mobile}</div>
                            </div>
                            <div style={{display:'flex', gap:'10px'}}>
                                {!addr.isPrimary && <button className={styles.toggleBtn} onClick={() => handleSetPrimary(idx)}>Make Primary</button>}
                                {addresses.length > 1 && <button className={styles.btnGhost} onClick={() => handleRemoveAddress(idx)}><X size={16}/></button>}
                            </div>
                        </div>
                    ))}

                    <div style={{marginTop:'1rem', borderTop:'1px solid #f1f5f9', paddingTop:'1rem'}}>
                        <div className={styles.gridCols2}>
                          <input 
                              type="tel"
                              placeholder="Mobile (10 digits)" 
                              value={currentMobileInput} 
                              onChange={e => setCurrentMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))} 
                              className={styles.input}
                          />
                          <input 
                              type="tel"
                              placeholder="Landline" 
                              value={currentLandlineInput} 
                              onChange={e => setCurrentLandlineInput(e.target.value)} 
                              className={styles.input}
                          />                        </div>
                        <div className={styles.inputWithIcon} style={{marginTop:'1rem'}}>
                            <MapPin className={styles.inputIcon} size={16}/>
                            <input 
                                className={`${styles.input} ${styles.inputPl}`} 
                                placeholder="Add Address..." 
                                value={currentAddressInput}
                                onChange={handleAddressSearchChange}
                            />
                            {addressResults.length > 0 && (
                                <ul className={styles.dropdownResults}>
                                    {addressResults.map(s => (
                                        <li key={s.place_id} className={styles.resultItem} onClick={() => handleSelectAddress(s)}>{s.description}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <button className={styles.btnOutline} onClick={handleAddAddress}><Plus size={16}/> Add Location</button>
                    </div>
                </div>
            </div>

            {/* Accreditation (RESTORED CERTIFICATIONS) */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><ShieldCheck color="#0891b2" /> Accreditation</div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.gridCols2}>
                        <div>
                            <label className={styles.label}>License Number</label>
                            <input value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} className={styles.input} />
                        </div>
                        <div>
                            <label className={styles.label}>Expiry</label>
                            <input type="date" value={licenseExpiry} onChange={e => setLicenseExpiry(e.target.value)} className={styles.input} />
                        </div>
                    </div>
                    <div>
                        <button 
                            className={`${styles.toggleBtn} ${isFullyInsured ? styles.toggleGreen : ''}`}
                            onClick={() => setIsFullyInsured(!isFullyInsured)}
                            style={{width:'100%', marginTop:'1rem'}}
                        >
                            {isFullyInsured ? '✔ I am Fully Insured' : 'No, I am not fully insured'}
                        </button>
                    </div>

                    {/* Restored Certifications Section */}
                    {availableCertifications.length > 0 && (
                      <div style={{marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9'}}>
                        <label className={styles.label}>Certifications</label>
                        <div className={styles.flexWrap}>
                          {availableCertifications.map(cert => (
                            <button
                                key={cert.certificationId}
                                onClick={() => handleCertificationChange(cert.certificationId)}
                                style={{
                                    background: selectedCertifications.includes(cert.certificationId) ? '#eff6ff' : 'white',
                                    borderColor: selectedCertifications.includes(cert.certificationId) ? '#2563eb' : '#cbd5e1',
                                    color: selectedCertifications.includes(cert.certificationId) ? '#1e40af' : '#64748b'
                                }}
                                className={styles.toggleBtn}
                            >
                                {cert.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
            </div>

            {/* RESTORED: Qualifications & Memberships */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><GraduationCap color="#db2777" /> Qualifications & Memberships</div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.gridCols2}>
                        <div>
                            <label className={styles.label}>Years of Experience</label>
                            <input type="number" min="1" value={yearsExperience} onChange={e => setYearsExperience(e.target.value)} className={styles.input} />
                        </div>
                        <div>
                            <label className={styles.label}>Registration Number (if applicable)</label>
                            <input value={registrationNumber} onChange={e => setRegistrationNumber(e.target.value)} className={styles.input} />
                        </div>
                    </div>

                    {/* Qualifications List */}
                    <div style={{marginTop: '1.5rem'}}>
                        <label className={styles.label}>Qualifications</label>
                        {availableQualifications.length === 0 && <p style={{fontSize:'0.85rem', color:'#94a3b8'}}>Select a profession to see relevant qualifications.</p>}
                        <div className={styles.flexWrap}>
                           {availableQualifications.map(q => (
                               <div key={q.qualificationId} onClick={() => handleQualificationChange(q.qualificationId)} 
                                    style={{
                                        padding: '8px 14px', borderRadius: '6px', border: '1px solid', cursor: 'pointer', fontSize: '0.9rem',
                                        background: selectedQualifications.includes(q.qualificationId) ? '#eff6ff' : 'white',
                                        color: selectedQualifications.includes(q.qualificationId) ? '#1e40af' : '#64748b',
                                        borderColor: selectedQualifications.includes(q.qualificationId) ? '#2563eb' : '#cbd5e1'
                                    }}>
                                    {q.name}
                               </div>
                           ))}
                        </div>
                    </div>

                     {/* Memberships List 
                     <div style={{marginTop: '1.5rem'}}>
                        <label className={styles.label}>Industry Memberships</label>
                        <div className={styles.flexWrap}>
                           {availableMemberships.map(m => (
                               <div key={m.membershipId} onClick={() => handleMembershipChange(m.membershipId)}
                                    style={{
                                        padding: '8px 14px', borderRadius: '6px', border: '1px solid', cursor: 'pointer', fontSize: '0.9rem',
                                        background: selectedMemberships.includes(m.membershipId) ? '#eff6ff' : 'white',
                                        color: selectedMemberships.includes(m.membershipId) ? '#1e40af' : '#64748b',
                                        borderColor: selectedMemberships.includes(m.membershipId) ? '#2563eb' : '#cbd5e1'
                                    }}>
                                    {m.name}
                               </div>
                           ))}
                        </div>
                    </div>

*/}



                </div>
            </div>

            {/* Image Upload */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><Camera color="#ea580c" /> Profile Image</div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.uploadArea}>
                        <label htmlFor="file-upload" className={styles.btnOutline} style={{width:'auto', display:'inline-block'}}>
                            Browse Files
                            <input id="file-upload" type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
                        </label>
                        {preview && (
                            <div style={{display:'flex', justifyContent:'center'}}>
                                <img src={preview} className={styles.previewImage} alt="Preview" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cover Photo Upload */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}><Camera color="#ea580c" /> Cover Photo</div>
                </div>
                <div className={styles.cardContent}>
                    <div style={{ marginBottom: '1rem' }}>
                        <p style={{fontSize: '0.9rem', color: '#64748b'}}>Upload a wide landscape photo (1200x400px)</p>
                        <div style={{
                            width: '100%', 
                            height: '150px', 
                            borderRadius: '8px', 
                            border: '2px dashed #cbd5e1',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            overflow: 'hidden', 
                            backgroundColor: '#f8fafc', 
                            position: 'relative',
                            backgroundImage: coverImageBase64 ? `url(${coverImageBase64})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            marginTop: '10px'
                        }}>
                            {!coverImageBase64 && (
                                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                                    <Camera size={32} />
                                </div>
                            )}
                            <input type="file" accept="image/*" onChange={handleCoverUpload}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className={styles.colSidebar}>
            <div className={styles.card} style={{position:'sticky', top:'80px'}}>
                <div className={styles.sidebarHeader}>
                    <h3 className={styles.sidebarTitle}>Summary</h3>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.sidebarRow}>
                        <div className={styles.sidebarLabel}>BUSINESS NAME</div>
                        <div className={styles.sidebarValue}>{businessName}</div>
                    </div>
                    <div className={styles.sidebarRow}>
                        <div className={styles.sidebarLabel}>LOCATIONS</div>
                        <div className={styles.sidebarValue}>{addresses.length} added</div>
                    </div>
                    <div className={styles.sidebarRow}>
                        <div className={styles.sidebarLabel}>SERVICES</div>
                        <div className={styles.sidebarValue}>{selectedServices.length} selected</div>
                    </div>
                    
                    <button onClick={handleSubmit} className={styles.btnPrimary}>
                        <ShieldCheck size={20}/> Save Changes
                    </button>
                    
                    <button onClick={() => navigate(-1)} className={styles.btnOutline}>
                        Cancel
                    </button>
                </div>
            </div>

            <div className={`${styles.card} ${styles.tipsCard}`}>
                <div className={styles.cardContent}>
                    <span className={styles.tipsEmoji}>💡</span>
                    <h4 style={{margin:'0 0 0.5rem 0', color:'#1e3a8a'}}>Pro Tips</h4>
                    <ul className={styles.tipsList}>
                        <li>Complete profiles get 80% more leads.</li>
                        <li>Upload a high quality logo.</li>
                        <li>List all your services to appear in more searches.</li>
                    </ul>
                </div>
            </div>
          </div>

        </div>
      </div>

      {/* SERVICE MODAL */}
      {showAddServicePopup && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50}}>
            <div className={styles.card} style={{width:'400px', maxWidth:'90%'}}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>Add Custom Service</div>
                    <button className={styles.btnGhost} onClick={() => setShowAddServicePopup(false)}><X size={18}/></button>
                </div>
                <div className={styles.cardContent}>
                    <div>
                        <label className={styles.label}>Select Profession Category</label>
                        <select 
                            className={styles.input} 
                            value={newService.professionId} 
                            onChange={e => setNewService({...newService, professionId: e.target.value})}
                        >
                            <option value="">Select...</option>
                            {selectedProfessions.map(p => <option key={p.id || p.professionId} value={p.id || p.professionId}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={styles.label}>Service Name</label>
                        <input 
                            className={styles.input} 
                            placeholder="e.g. Custom Joinery" 
                            value={newService.name} 
                            onChange={e => setNewService({...newService, name: e.target.value})}
                        />
                    </div>
                    <button className={styles.btnPrimary} onClick={handleAddNewService} disabled={addServiceLoading}>
                        {addServiceLoading ? <Loader2/> : 'Add Service'}
                    </button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default BusinessOnboardingNew;